<template>
    <!-- 쿠팡 계정 한 줄 — 멀티계정관리 창과 자동수집 창이 같이 쓴다(동작은 services/coupangAccountFlow.js).
         멀티계정관리 = 카드 한 장(div) · compact = 자동수집 창용 표 한 줄(tr): 쿠플러스 계정 칸·전환·⋯ 메뉴를 뺀다. -->
    <component :is="compact ? 'tr' : 'div'" class="account-row"
               :class="{
                   'account-row--card': !compact,
                   'account-row--current': row.isCurrent && !compact,
                   'account-row--edited': edited && !compact,
                   'account-row--busy': row.busy,
               }">
        <component :is="cellTag" v-if="!compact" class="account-row__cell account-row__cell--acct">
            <template v-if="row.isNew">
                <input type="email" class="form-control form-control-sm mb-1" placeholder="쿠플러스 아이디"
                       aria-label="쿠플러스 아이디" v-model.trim="row.couplusEmail" autocomplete="off" :disabled="disabled">
                <input :type="row.showPw ? 'text' : 'password'" class="form-control form-control-sm"
                       placeholder="쿠플러스 비밀번호" aria-label="쿠플러스 비밀번호"
                       v-model="row.couplusPw" autocomplete="new-password" :disabled="disabled">
            </template>
            <template v-else-if="row.email">
                <div class="account-row__name">
                    {{ row.companyid || row.email }}
                    <span v-if="row.isCurrent" class="account-row__tag">사용 중</span>
                </div>
                <div class="account-row__sub">{{ row.email }}</div>
            </template>
            <div v-else class="account-row__sub">{{ row.suppliercode }} (연결된 쿠플러스 계정 없음)</div>
        </component>
        <component :is="cellTag" class="account-row__cell account-row__cell--id">
            <input type="text" class="form-control form-control-sm" placeholder="쿠팡 아이디"
                   aria-label="쿠팡 아이디" v-model.trim="row.cpId" autocomplete="off"
                   :disabled="disabled || (!row.isNew && !row.suppliercode)">
        </component>
        <component :is="cellTag" class="account-row__cell account-row__cell--pw">
            <div class="account-row__pw">
                <input :type="row.showPw ? 'text' : 'password'" class="form-control form-control-sm"
                       :placeholder="row.cpHasCred ? '저장됨 · 바꿀 때만 입력' : '쿠팡 비밀번호'"
                       aria-label="쿠팡 비밀번호" v-model="row.cpPw" autocomplete="new-password"
                       :disabled="disabled || (!row.isNew && !row.suppliercode)">
                <button type="button" class="account-row__icon-btn"
                        :aria-label="row.showPw ? '비밀번호 숨기기' : '비밀번호 보기'"
                        @click="row.showPw = !row.showPw">
                    <i :class="row.showPw ? 'ri-eye-off-line' : 'ri-eye-line'" aria-hidden="true"></i>
                </button>
            </div>
        </component>
        <component :is="cellTag" class="account-row__cell account-row__cell--status">
            <!-- 자동수집 창 = 상태등 한 줄 -->
            <div v-if="compact" class="account-row__lights account-row__lights--compact">
                <span v-for="ch in channelKeys" :key="ch.key" class="account-row__light" :title="light(ch.key).text">
                    <span v-if="row.busy && light(ch.key).tone === 'wait'"
                          class="account-row__spinner" role="status" aria-label="진행 중"></span>
                    <span v-else :class="['account-row__dot', 'account-row__dot--' + light(ch.key).tone]"
                          aria-hidden="true">{{ toneMark[light(ch.key).tone] }}</span>
                    {{ ch.label }}
                    <span v-if="light(ch.key).text === '권한 없음'" class="account-row__note">(권한 없음)</span>
                </span>
            </div>
            <!-- 멀티계정관리 = 배지(채널마다 하나 · 자세한 상태는 마우스를 올리면) -->
            <div v-else class="account-row__badges">
                <span v-if="edited && !row.isNew" class="account-row__bd account-row__bd--warn">저장 안 됨</span>
                <span v-if="noLoginYet" class="account-row__bd">아직 로그인 정보 없음</span>
                <template v-else>
                    <span v-for="ch in channelKeys" :key="ch.key"
                          :class="['account-row__bd', 'account-row__bd--' + light(ch.key).tone]"
                          :title="ch.label + ': ' + light(ch.key).text">
                        <span v-if="row.busy && light(ch.key).tone === 'wait'"
                              class="account-row__spinner" role="status" aria-label="진행 중"></span>
                        <span v-else-if="toneMark[light(ch.key).tone]" aria-hidden="true">{{ toneMark[light(ch.key).tone] }}</span>
                        {{ ch.label }}
                        <span v-if="light(ch.key).text === '권한 없음'">권한 없음</span>
                        <span class="sr-only">: {{ light(ch.key).text }}</span>
                    </span>
                </template>
                <!-- 자동수집 일정 개수 — 마지막 수집·다음 예정은 마우스를 올리면 -->
                <span v-if="autoCount || autoSummary" class="account-row__bd account-row__bd--info"
                      :title="autoSummary">자동수집{{ autoCount ? ' ' + autoCount : '' }}</span>
            </div>
            <div v-if="row.message" class="account-row__reason"
                 :class="{ 'account-row__reason--bad': row.messageBad }">{{ row.message }}</div>
        </component>
        <component :is="cellTag" class="account-row__cell account-row__cell--actions"
                   :class="{ 'text-right text-nowrap': compact }">
            <!-- 순서: 저장(고친 줄에만) · 전환 · 로그인 확인 · ⋯(자동수집 설정 · Open API 설정 · 삭제) -->
            <b-button v-if="compact || edited" size="sm" variant="primary" :class="{ 'mr-1': compact }"
                      :disabled="disabled || !dirty" @click="$emit('check', row)">
                <span v-if="row.busy" class="account-row__spinner account-row__spinner--light mr-1"
                      role="status" aria-label="저장 중"></span>
                저장
            </b-button>
            <b-button v-if="!compact && row.email && !row.isCurrent && !row.isNew" size="sm" variant="outline-secondary"
                      class="account-row__btn" :disabled="disabled || !!switchBlockedReason"
                      :title="switchBlockedReason" @click="$emit('switch', row)">전환</b-button>
            <!-- 안 A: 저장은 아이디·비밀번호만 저장한다. 쿠팡 로그인 확인(필요하면 다른 계정 로그인 보관 후 전환)은 이 버튼 -->
            <b-button size="sm" variant="outline-secondary" class="account-row__btn"
                      :disabled="disabled || row.busy || row.isNew || !(row.cpHasCred || (row.cpId && row.cpPw))"
                      title="이 계정으로 쿠팡 로그인을 확인합니다 (먼저 확인창이 뜹니다)"
                      @click="$emit('login-check', row)">로그인 확인</b-button>
            <b-button v-if="!compact && row.isNew" size="sm" variant="link" class="account-row__cancel"
                      :disabled="disabled" @click="$emit('remove', row)">취소</b-button>
            <!-- 드문 일은 ⋯ 메뉴로 — 자동수집 설정 · Open API 설정 · 삭제 -->
            <b-dropdown v-if="!compact && !row.isNew" size="sm" variant="outline-secondary" right no-caret
                        boundary="window" toggle-class="account-row__btn account-row__more"
                        menu-class="account-row__menu" :disabled="disabled">
                <template #button-content>
                    <i class="ri-more-fill" aria-hidden="true"></i><span class="sr-only">더보기</span>
                </template>
                <!-- 자동수집 설정 — 그 계정의 쿠플러스 계정(회사ID·판매자코드)이 있어야 저장할 곳이 생긴다 -->
                <b-dropdown-item-button :disabled="!canAutoCollect" @click="$emit('auto-collect', row)">
                    자동수집 설정
                    <small v-if="!canAutoCollect" class="account-row__menu-note">쿠플러스 계정 연결 필요</small>
                </b-dropdown-item-button>
                <!-- 쿠팡 Open API 키 — 이 PC 의 couplus 에 이 판매자코드 키가 있나(설정은 «Open API 설정» 창 · KG ontology/build/tool.js 가 이 줄 글자를 읽는다) -->
                <b-dropdown-item-button v-if="showOpenApi" @click="$emit('open-api', row)">Open API 설정<small :class="['account-row__menu-note', 'account-row__openapi--' + openApiState.tone]">{{ openApiState.text }}</small></b-dropdown-item-button>
                <b-dropdown-divider />
                <b-dropdown-item-button variant="danger" @click="$emit('remove', row)">삭제</b-dropdown-item-button>
            </b-dropdown>
        </component>
    </component>
</template>

<script>
import { CHANNEL_KEYS, TONE_MARK, lightOf, isDirty, autoCollectCount, autoCollectSummary, openApiStatusOf } from '@/services/coupangAccountFlow';

export default {
    name: 'CoupangAccountRow',
    props: {
        // 줄 상태(services/coupangAccountFlow.js makeRow). 입력은 이 객체에 바로 쓴다
        row: {
            type: Object,
            required: true,
        },
        // 다른 줄이 확인 중이면 막는다
        disabled: {
            type: Boolean,
            default: false,
        },
        // 수집·저장 중이면 전환을 막는 이유. 빈 문자열이면 전환 가능
        switchBlockedReason: {
            type: String,
            default: '',
        },
        // 자동수집 창용: 표 한 줄(tr)로 그리고 쿠플러스 계정 칸·전환·⋯ 메뉴를 뺀다
        compact: {
            type: Boolean,
            default: false,
        },
        // 쿠팡 Open API 키 상태(이 PC · 판매자코드 기준). undefined = 표시 안 함(권한 밖·확장 없음)
        openApi: {
            type: Object,
            default: undefined,
        },
    },
    data() {
        return {
            channelKeys: CHANNEL_KEYS,
            toneMark: TONE_MARK,
        };
    },
    computed: {
        cellTag() {
            return this.compact ? 'td' : 'div';
        },
        dirty() {
            return isDirty(this.row);
        },
        // 고친 줄 — 「저장」 단추와 «저장 안 됨» 배지는 이 줄에만 보인다(새 줄 · 비밀번호 입력 · 아이디 바꿈)
        edited() {
            const r = this.row;
            return !!(r.isNew || r.cpPw || (r.cpId || '') !== (r.cpSavedId || ''));
        },
        // 저장된 쿠팡 로그인이 아직 없고 확인한 적도 없다 — 채널 배지 대신 한 개만
        noLoginYet() {
            const r = this.row;
            return !r.cpHasCred && !r.busy && !r.result && !r.lastCheck && !r.status;
        },
        // 「Open API 설정」 — 멀티계정관리 창의 판매자코드 있는 저장된 줄에만(권한 밖이면 openApi 를 안 준다)
        showOpenApi() {
            return !this.compact && !this.row.isNew && !!this.row.suppliercode && this.openApi !== undefined;
        },
        openApiState() {
            return openApiStatusOf(this.openApi);
        },
        canAutoCollect() {
            return !!(this.row.companyid && this.row.suppliercode && this.row.email);
        },
        autoCount() {
            return this.compact ? null : autoCollectCount(this.row);
        },
        autoSummary() {
            return this.compact ? '' : autoCollectSummary(this.row);
        },
    },
    methods: {
        light(key) {
            return lightOf(this.row, key);
        },
    },
};
</script>

<style lang="scss">
// 앱 테마 변수(값만 가져온다 — CSS 를 내지 않는 파일들)
@import '~@/assets/scss/bootstrap/functions';
@import '~@/assets/scss/custom_variable';
@import '~@/assets/scss/variable';

// ⋯ 메뉴는 b-dropdown 이 몸통 밖(boundary=window)에 띄워도 이 컴포넌트 안에 그려진다 — 다만 scoped 로는 하위 컴포넌트 속에 못 닿는다
.account-row__menu {
    min-width: 200px;

    .dropdown-item {
        display: flex;
        justify-content: space-between;
        align-items: center;
        gap: 12px;
        font-size: 13px;
    }
    .dropdown-item.text-danger { color: #b3261e !important; }
}
body.dark .account-row__menu .dropdown-item.text-danger { color: $danger !important; }
.account-row__menu-note {
    font-size: 12px;
    color: #6b7585;
}
// 줄 버튼은 무채색 — 색은 «저장»(고친 줄에만)과 상태 배지에만 남긴다. ⋯ 토글은 b-dropdown 안쪽이라 scoped 로는 못 닿는다
.account-row__btn.btn,
.account-row__more.btn {
    border-color: #cfd5de;
    background: #fff;
    color: #16202e;

    &:hover:not(:disabled),
    &:focus,
    .show > &,
    &:not(:disabled):not(.disabled):active {
        border-color: #9aa3b0;
        background: #f3f5f8;
        color: #16202e;
    }
    &:disabled {
        border-color: #e3e7ed;
        background: #fff;
        color: #a5adb9;
    }
}
.account-row__more.btn {
    padding: 3px 8px;
    line-height: 1;
    font-size: 16px;
}
body.dark .account-row__btn.btn,
body.dark .account-row__more.btn {
    border-color: $gray-700;
    background: $dark-gray;
    color: $gray-200;

    &:disabled { color: $gray-600; }
}

// 다크 테마 = body.dark (ModeSwitch 가 켠다, 공통은 assets/scss/backend/layouts/_dark.scss).
// 아래 scoped 스타일이 직접 정한 밝은 배경·회색 글씨만 같은 테마 변수로 맞춘다. 라이트 테마는 그대로.
body.dark .account-row {
    &.account-row--busy { background: $dark-warning; }
    &.account-row--card {
        background: $dark-gray;
        border-color: $dark-border-color;
    }
    &.account-row--card.account-row--current { border-color: $primary; box-shadow: 0 0 0 3px $dark-primary; }
    &.account-row--card.account-row--edited { border-color: $warning; }
    &:not(.account-row--card).account-row--current { background: $dark-primary; }

    .account-row__sub,
    .account-row__note,
    .account-row__reason,
    .account-row__icon-btn {
        color: $gray-500;
    }
    .account-row__reason--bad { color: $danger; }
    .account-row__tag { color: $primary; background: $dark-primary; }
    .account-row__bd { background: $dark-secondary; color: $gray-400; }
    .account-row__bd--ok { background: $dark-success; color: $success; }
    .account-row__bd--bad { background: $dark-danger; color: $danger; }
    .account-row__bd--wait,
    .account-row__bd--warn { background: $dark-warning; color: $warning; }
    .account-row__bd--info { background: $dark-info; color: $info; }
    .account-row__dot--none {
        border-color: $gray-500;
        color: $gray-500;
    }
}
body.dark .account-row__openapi--ok { color: $success; }
body.dark .account-row__openapi--bad { color: $danger; }
body.dark .account-row__menu-note { color: $gray-500; }
</style>

<style lang="scss" scoped>
@keyframes account-row-spin {
    to { transform: rotate(360deg); }
}

@media (prefers-reduced-motion: reduce) {
    .account-row__spinner { animation-duration: 2s; }
}

.account-row {
    &--current:not(.account-row--card) {
        background: #f5f9ff;
    }

    &--busy {
        background: #fffbea;
    }

    // 멀티계정관리 — 계정 하나 = 카드 한 장. 칸 순서는 화면에서만 바꾼다(입력 칸이 먼저 오는 DOM 순서 = 탭 순서 유지)
    &--card {
        display: grid;
        grid-template-columns: minmax(140px, 180px) minmax(200px, 1fr) minmax(120px, 170px) minmax(140px, 190px) auto;
        grid-template-areas: "acct status id pw actions";
        gap: 8px 14px;
        align-items: center;
        margin: 0 0 8px;
        padding: 12px 14px;
        background: #fff;
        border: 1px solid #e3e7ed;
        border-radius: 10px;
        transition: border-color 0.15s ease-out, box-shadow 0.15s ease-out;

        &.account-row--current {
            border-color: #b9cdf3;
            box-shadow: 0 0 0 3px #e9f0fd;
        }

        &.account-row--edited {
            border-color: #e9c98c;
        }

        &.account-row--busy {
            background: #fffdf6;
        }

        .account-row__cell--acct { grid-area: acct; min-width: 0; }
        .account-row__cell--status { grid-area: status; min-width: 0; }
        .account-row__cell--id { grid-area: id; min-width: 0; }
        .account-row__cell--pw { grid-area: pw; min-width: 0; }
        .account-row__cell--actions {
            grid-area: actions;
            display: flex;
            align-items: center;
            justify-content: flex-end;
            gap: 6px;
            white-space: nowrap;
        }

        .account-row__pw .form-control { min-width: 0; }

        @media (max-width: 1100px) {
            grid-template-columns: minmax(140px, 1fr) minmax(120px, 1fr) minmax(140px, 1fr);
            grid-template-areas:
                "acct status status"
                "id pw actions";
        }

        @media (max-width: 640px) {
            grid-template-columns: 1fr;
            grid-template-areas: "acct" "status" "id" "pw" "actions";

            .account-row__cell--actions { justify-content: flex-start; flex-wrap: wrap; }
        }
    }

    &__btn {
        font-weight: 600;
    }

    &__cancel {
        color: #6b7585;
    }

    &__name {
        font-weight: 600;
        overflow-wrap: anywhere;
    }

    &__tag {
        margin-left: 6px;
        padding: 1px 6px;
        border-radius: 4px;
        font-size: 11px;
        font-weight: 700;
        color: #164aa8;
        background: #e9f0fd;
        white-space: nowrap;
    }

    &__sub {
        font-size: 12px;
        color: #5f6368;
        overflow-wrap: anywhere;
    }

    &__pw {
        display: flex;
        align-items: center;
        gap: 4px;
    }

    &__icon-btn {
        border: 0;
        background: transparent;
        padding: 4px;
        min-width: 32px;
        min-height: 32px;
        cursor: pointer;
        color: #5f6368;

        &:focus-visible {
            outline: 2px solid #1a73e8;
            border-radius: 4px;
        }
    }

    // ── 멀티계정관리 배지 ──
    &__badges {
        display: flex;
        flex-wrap: wrap;
        gap: 5px;
    }

    &__bd {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        padding: 2px 9px;
        border-radius: 999px;
        font-size: 12px;
        font-weight: 600;
        line-height: 1.5;
        white-space: nowrap;
        color: #4a5566;
        background: #eef1f5;

        &--ok { color: #1d7a45; background: #e6f5ec; }
        &--bad { color: #b3261e; background: #fdeceb; }
        &--wait,
        &--warn { color: #8f5506; background: #fdf3e2; }
        &--info { color: #0e6b7a; background: #e3f4f7; }
    }

    // ── 자동수집 창(compact) 상태등 ──
    &__lights {
        display: flex;
        flex-wrap: wrap;
        gap: 10px;
        font-size: 13px;
    }

    // 자동수집 창은 폭이 좁다 — 세 채널을 한 줄에 둔다
    &__lights--compact {
        flex-wrap: nowrap;
        gap: 8px;
    }

    &__light {
        display: inline-flex;
        align-items: center;
        gap: 4px;
        white-space: nowrap;
    }

    &__dot {
        display: inline-flex;
        align-items: center;
        justify-content: center;
        width: 16px;
        height: 16px;
        border-radius: 50%;
        font-size: 10px;
        line-height: 1;
        color: #fff;

        &--ok { background: #1e8e3e; }
        &--wait { background: #b06000; }
        &--bad { background: #d93025; }
        &--none { background: transparent; border: 1px solid #9aa0a6; color: #5f6368; }
    }

    // 이 앱 테마에는 부트스트랩 스피너 스타일이 없어 직접 그린다
    &__spinner {
        display: inline-block;
        width: 12px;
        height: 12px;
        border: 2px solid #b06000;
        border-right-color: transparent;
        border-radius: 50%;
        vertical-align: middle;
        animation: account-row-spin 0.75s linear infinite;

        &--light {
            border-color: #fff;
            border-right-color: transparent;
        }
    }

    &__note {
        color: #5f6368;
        font-size: 11px;
    }

    &__reason {
        margin-top: 4px;
        font-size: 12px;
        color: #5f6368;

        &--bad {
            color: #b3261e;
        }
    }

    &__openapi--ok { color: #1d7a45; }
    &__openapi--bad { color: #b3261e; }
}

td {
    vertical-align: middle;
}
</style>
